import { useAnchorCell } from './anchorCell';
import { describeOverflow } from './overflow';
import { Tooltip } from '../Tooltip';

/*
  Spelled out rather than interpolated. Tailwind scans source for literal class names, so a
  `text-${size}` template is invisible to it — it works here only because both utilities
  happen to be written literally elsewhere, which is not a guarantee to build on.
*/
const SIZES = {
  sm: 'text-sm',
  xs: 'text-xs',
} as const;

/**
 * Whether a value is a quantity that reads as nothing.
 *
 * Zero, and every way of writing it a formatter produces: `0`, `0.00`, `0,000.00`, `0 ms`,
 * `0 B`, `0%`. Everything that is not a digit, a sign or a decimal point is stripped first,
 * because a formatted quantity arrives with its separators and its unit already attached.
 *
 * That is safe ONLY because the caller opted in: `numeric` is a declaration that the column
 * holds quantities, so stripping a suffix cannot turn a code, a year or a part number into a
 * zero. Sniffing every value instead is what would.
 */
function isZeroQuantity(value: string): boolean {
  const bare = value.replace(/[^\d.+-]/gu, '');

  if (bare === '') {
    return false;
  }

  const parsed = Number(bare);

  return Number.isFinite(parsed) && parsed === 0;
}

/**
 * Plain text cell — reference §4.
 *
 * Multi-word values wrap; a single long token ellipsizes and carries a `title` so the full
 * value stays reachable. Empty renders an em-dash rather than a blank cell. In the anchor
 * column the value darkens, because there it is the row's click target.
 *
 * A `numeric` cell holding zero is MUTED — see the prop.
 */
export function TextCell({
  value,
  mono = false,
  numeric = false,
  size = 'sm',
  strong = false,
  clamp = false,
  code = null,
  trailingCode = null,
}: {
  value: string | null | undefined;
  mono?: boolean;
  /**
   * Says this column holds a quantity — a count, a rate, an amount.
   *
   * The one thing it changes: **zero renders muted**, in the same tone as an absent value.
   * A zero is a real answer and stays legible, but it is the answer that means "nothing
   * here", and in a column of quantities the numbers that matter are the ones that are not
   * zero — muting it lets the eye find them without reading the column.
   *
   * It is a declaration by the CALL SITE rather than something inferred from the value: a
   * code, a year or a part number can be "0" and must not be dimmed for it.
   */
  numeric?: boolean;
  /**
   * `xs` matches `<DateCell>`. Use it for a column that sits beside a date and has to read
   * as the same kind of value — the shell's Created / Created by pair, for one.
   */
  size?: 'sm' | 'xs';

  /**
   * Draws the value a step heavier — for the ONE column of a band that the others qualify.
   *
   * A named step rather than a `className`, for the reason `<TextField trailingWidth>` is
   * one: a caller passing its own utility competes with the built-in classes at equal
   * specificity, and which wins would depend on the stylesheet order rather than on the
   * call site.
   *
   * <b>Weight only.</b> No tone and no size, so a muted zero still mutes and the column
   * keeps the rhythm its tabular figures are there for. Reach for it once per band at most:
   * a table where several figures are heavy has none that are.
   */
  strong?: boolean;

  /**
   * Holds the value to ONE LINE — ellipsis and a tooltip — even though it has spaces to
   * break at.
   *
   * `describeOverflow` wraps a multi-word value deliberately, because an ellipsized sentence
   * loses its end with no way to get it back. That is right for prose in a cell and wrong for
   * a MERGED ANCHOR, whose own rule is *one line, one header, one width* precisely so row
   * heights stay uniform — a title that wraps to two lines makes one row taller than the
   * forty around it, which is what a table is scanned against.
   *
   * <b>Nothing is unreachable</b>: clamping always attaches the `title`, where wrapping never
   * needs one. Reach for it on an anchor that joins two values, and nowhere else — a data
   * cell that clamps is a cell hiding the end of its own value for no reason.
   *
   * Product call, 29 Sep 2026: *concat the items in the PR number. apply max width as well.*
   */
  clamp?: boolean;
  /**
   * A leading CODE for a MERGED ANCHOR, kept in the mono face while the value beside it stays
   * prose. Joined with the product's own ` — `, which lives here so two call sites cannot
   * punctuate one pattern differently.
   *
   * **It exists because `value` is a string and a string has ONE face.** The merged anchor's
   * rule is *one line, one header, one width* — and the PR register is the first of them whose
   * two halves are different KINDS of value: `PR-26-00009` is a code and `Google Gemini,
   * Nissin Spicy Seafood` is a run of names. Passing `mono` for the pair put the product names
   * in the machine face; passing nothing would have put the number in the body face.
   *
   * Product call, 01 Oct 2026: *the table list clickable title should not be font-mono —
   * except for the PR no.* That is the codes-are-mono rule applied to half a cell, so reach
   * for it only where the leading half genuinely is a code.
   */
  code?: string | null;
  /**
   * `code`'s other end — a machine value CLOSING a merged anchor, in the mono face, after the
   * same ` — `. The salary tranche's title ends on its effective date (product call, 16 Oct
   * 2026: "concat the type before the tranche, then concat the effective date"), and a date is
   * always mono.
   */
  trailingCode?: string | null;
}) {
  const anchor = useAnchorCell();

  /*
    THE JOINED STRING is what the tooltip and the overflow test read, because it is what the
    reader sees — measuring the prose half alone would clamp against the wrong length.
  */
  const joined = [code, value, trailingCode].filter(Boolean).join(' — ') || null;
  const split = Boolean(code || trailingCode);

  if (!joined) {
    // The placeholder keeps the column's typeface. In a mono column an em-dash set in the
    // body face sits on a different rhythm, and the column stops reading as one column.
    return (
      <span className={`${SIZES[size]} text-slate-400 ${mono ? 'font-mono tabular-nums' : ''}`}>—</span>
    );
  }

  const overflow = clamp
    ? { className: 'truncate' as const, title: joined }
    : describeOverflow(joined);
  const muted = numeric && isZeroQuantity(joined);

  const cell = (
    <span
      className={[
        `block ${SIZES[size]}`,
        anchor
          ? 'font-medium text-blue-500'
          : muted
            ? 'text-slate-300'
            : 'text-slate-500',
        /*
          THE CELL'S OWN FACE IS THE PROSE FACE WHERE A `code` LEADS IT. `mono` on the wrapper
          would reach the names beside the code through inheritance, which is the defect this
          prop exists to fix — the register passed `mono` for the pair and put `Google Gemini,
          Nissin Spicy Seafood` in the machine face.
        */
        mono && !split ? 'font-mono tabular-nums' : '',
        strong ? 'font-medium' : '',
        overflow.className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      {split ? (
        <>
          {code ? <span className="font-mono tabular-nums">{code}</span> : null}
          {code && value ? ' — ' : null}
          {value}
          {trailingCode && (code || value) ? ' — ' : null}
          {trailingCode ? <span className="font-mono tabular-nums">{trailingCode}</span> : null}
        </>
      ) : (
        value
      )}
    </span>
  );

  /*
    Wrapped ONLY when there is something to say. A cell whose value fits needs no tooltip,
    and wrapping every cell would put a portal-rendering component in each of the couple of
    hundred cells a full table draws.
  */
  return overflow.title ? (
    <Tooltip label={overflow.title} block>
      {cell}
    </Tooltip>
  ) : (
    cell
  );
}
